// Invitation Design Types

export interface InvitationDesign {
  id: string;
  invitationTypeId: string;
  templateId?: string;
  customizations: InvitationCustomizations;
  createdAt: number;
  updatedAt: number;
}

export interface InvitationCustomizations {
  typography: TypographySettings;
  colors: ColorSettings;
  borders: BorderSettings;
  layout: LayoutSettings;
  images: ImageSettings;
  content: ContentSettings;
}

export interface TypographySettings {
  headingFont: FontFamily;
  bodyFont: FontFamily;
  accentFont: FontFamily;
  fontSize: {
    coupleNames: number;
    eventTitle: number;
    body: number;
    details: number;
  };
}

export interface FontFamily {
  family: string;
  weight: number;
  style: 'normal' | 'italic';
  category: 'serif' | 'sans-serif' | 'script' | 'display' | 'condensed';
  stretch?: 'ultra-condensed' | 'extra-condensed' | 'condensed' | 'semi-condensed' | 'normal' | 'semi-expanded' | 'expanded' | 'extra-expanded' | 'ultra-expanded';
}

export interface ColorSettings {
  primary: string;
  secondary: string;
  text: string;
  background: string;
  accent: string;
  borders: string;
}

export interface BorderSettings {
  style: 'none' | 'solid' | 'dotted' | 'ornamental' | 'floral' | 'geometric';
  width: number;
  radius: number;
  padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

export interface LayoutSettings {
  alignment: 'left' | 'center' | 'right';
  spacing: 'compact' | 'normal' | 'relaxed';
  orderOfElements: string[]; // Array of element IDs in order
}

export interface ImageSettings {
  headerImage?: string;
  backgroundImage?: string;
  backgroundOpacity: number;
  decorativeElements: DecorativeElement[];
}

export interface DecorativeElement {
  type: 'flourish' | 'border' | 'corner' | 'divider';
  svg?: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'corners';
}

export interface ContentSettings {
  greetingText?: string;
  coupleNames: {
    bride: string;
    groom: string;
    order: 'brideFirst' | 'groomFirst';
    separator: '&' | 'and' | '♥';
  };
  eventDetails: {
    showDate: boolean;
    showTime: boolean;
    showLocation: boolean;
    showDressCode: boolean;
  };
  customMessage?: string;
  rsvpText?: string;
}

// Preset Templates
export interface InvitationTemplate {
  id: string;
  name: string;
  category: 'classic' | 'modern' | 'rustic' | 'beach' | 'garden' | 'minimal';
  thumbnail: string;
  customizations: InvitationCustomizations;
}

// Available fonts for the designer
export const AVAILABLE_FONTS: Record<string, FontFamily> = {
  // Serif fonts
  playfair: { family: 'Playfair Display', weight: 400, style: 'normal', category: 'serif' },
  georgia: { family: 'Georgia', weight: 400, style: 'normal', category: 'serif' },
  merriweather: { family: 'Merriweather', weight: 300, style: 'normal', category: 'serif' },
  crimsonText: { family: 'Crimson Text', weight: 400, style: 'normal', category: 'serif' },
  cormorant: { family: 'Cormorant Garamond', weight: 400, style: 'normal', category: 'serif' },
  ebGaramond: { family: 'EB Garamond', weight: 400, style: 'normal', category: 'serif' },
  libreBaskerville: { family: 'Libre Baskerville', weight: 400, style: 'normal', category: 'serif' },
  oldStandardTT: { family: 'Old Standard TT', weight: 400, style: 'normal', category: 'serif' },
  
  // Sans-serif fonts
  montserrat: { family: 'Montserrat', weight: 400, style: 'normal', category: 'sans-serif' },
  openSans: { family: 'Open Sans', weight: 400, style: 'normal', category: 'sans-serif' },
  raleway: { family: 'Raleway', weight: 300, style: 'normal', category: 'sans-serif' },
  lato: { family: 'Lato', weight: 400, style: 'normal', category: 'sans-serif' },
  nunito: { family: 'Nunito', weight: 400, style: 'normal', category: 'sans-serif' },
  inter: { family: 'Inter', weight: 400, style: 'normal', category: 'sans-serif' },
  sourceSansPro: { family: 'Source Sans Pro', weight: 400, style: 'normal', category: 'sans-serif' },
  poppins: { family: 'Poppins', weight: 400, style: 'normal', category: 'sans-serif' },
  roboto: { family: 'Roboto', weight: 400, style: 'normal', category: 'sans-serif' },
  
  // Script fonts
  greatVibes: { family: 'Great Vibes', weight: 400, style: 'normal', category: 'script' },
  dancingScript: { family: 'Dancing Script', weight: 400, style: 'normal', category: 'script' },
  parisienne: { family: 'Parisienne', weight: 400, style: 'normal', category: 'script' },
  allura: { family: 'Allura', weight: 400, style: 'normal', category: 'script' },
  sacramento: { family: 'Sacramento', weight: 400, style: 'normal', category: 'script' },
  alexBrush: { family: 'Alex Brush', weight: 400, style: 'normal', category: 'script' },
  beautifulPeople: { family: 'The Girl Next Door', weight: 400, style: 'normal', category: 'script' },
  kaushan: { family: 'Kaushan Script', weight: 400, style: 'normal', category: 'script' },
  satisfy: { family: 'Satisfy', weight: 400, style: 'normal', category: 'script' },
  
  // Display fonts
  abril: { family: 'Abril Fatface', weight: 400, style: 'normal', category: 'display' },
  bebas: { family: 'Bebas Neue', weight: 400, style: 'normal', category: 'display' },
  oswald: { family: 'Oswald', weight: 400, style: 'normal', category: 'display' },
  lobster: { family: 'Lobster', weight: 400, style: 'normal', category: 'display' },
  pacifico: { family: 'Pacifico', weight: 400, style: 'normal', category: 'display' },
  righteous: { family: 'Righteous', weight: 400, style: 'normal', category: 'display' },
  fredokaOne: { family: 'Fredoka One', weight: 400, style: 'normal', category: 'display' },
  bangers: { family: 'Bangers', weight: 400, style: 'normal', category: 'display' },
  
  // Condensed & Narrow fonts
  oswaldCondensed: { family: 'Oswald', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  bebasNeueCondensed: { family: 'Bebas Neue', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  robotoCondensed: { family: 'Roboto Condensed', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  openSansCondensed: { family: 'Open Sans Condensed', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  firaCondensed: { family: 'Fira Sans Condensed', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  ptSansNarrow: { family: 'PT Sans Narrow', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  archivoNarrow: { family: 'Archivo Narrow', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  abecondensed: { family: 'Abel', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  economica: { family: 'Economica', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  quicksand: { family: 'Quicksand', weight: 400, style: 'normal', category: 'condensed', stretch: 'semi-condensed' },
  fjalla: { family: 'Fjalla One', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  yanoneKaffeesatz: { family: 'Yanone Kaffeesatz', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  cabinCondensed: { family: 'Cabin Condensed', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  teko: { family: 'Teko', weight: 400, style: 'normal', category: 'condensed', stretch: 'extra-condensed' },
  impact: { family: 'Impact', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  squada: { family: 'Squada One', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  amaticSC: { family: 'Amatic SC', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  barlow: { family: 'Barlow Condensed', weight: 400, style: 'normal', category: 'condensed', stretch: 'condensed' },
  titillium: { family: 'Titillium Web', weight: 400, style: 'normal', category: 'condensed', stretch: 'semi-condensed' },
  exo: { family: 'Exo 2', weight: 400, style: 'normal', category: 'condensed', stretch: 'semi-condensed' },
};

// Default customization settings
export const DEFAULT_CUSTOMIZATIONS: InvitationCustomizations = {
  typography: {
    headingFont: AVAILABLE_FONTS.greatVibes,
    bodyFont: AVAILABLE_FONTS.openSans,
    accentFont: AVAILABLE_FONTS.montserrat,
    fontSize: {
      coupleNames: 48,
      eventTitle: 24,
      body: 16,
      details: 14,
    },
  },
  colors: {
    primary: '#D4A574',
    secondary: '#8B7355',
    text: '#333333',
    background: '#FFFEF9',
    accent: '#E8D5C4',
    borders: '#D4A574',
  },
  borders: {
    style: 'ornamental',
    width: 2,
    radius: 8,
    padding: {
      top: 48,
      right: 32,
      bottom: 48,
      left: 32,
    },
  },
  layout: {
    alignment: 'center',
    spacing: 'normal',
    orderOfElements: ['header', 'coupleNames', 'eventTitle', 'date', 'time', 'location', 'message', 'rsvp'],
  },
  images: {
    backgroundOpacity: 0.1,
    decorativeElements: [],
  },
  content: {
    greetingText: 'You are cordially invited to',
    coupleNames: {
      bride: '',
      groom: '',
      order: 'brideFirst',
      separator: '&',
    },
    eventDetails: {
      showDate: true,
      showTime: true,
      showLocation: true,
      showDressCode: true,
    },
    customMessage: 'We would be honored by your presence',
    rsvpText: 'Please RSVP by',
  },
};